import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  myLoggingIssuesSchema,
  sidebarViewSchema,
  toWireDate,
  type MyLoggingIssues,
  type SidebarView,
} from '@forecastify/shared';
import { useLiveInvalidation } from '../../live/use-live';
import { MY_LOGGING_TOPICS, SIDEBAR_TOPICS } from '../../live/topics';
import { useSession } from '../../session';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ What the chrome draws: counters and the two pinned lists.                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **One request for the whole sidebar**, not one per counter. `sidebar.service.ts` argues it: the
 * chrome outlives every route, so seven round trips would happen on every navigation.
 *
 * ⚠️⚠️ **IT RE-READS WHEN THE WORKSPACE CHANGES, AND WHEN SOMEBODY ASKS.** There is no polling — the
 * numbers are not urgent and a bell already polls once a minute — so a pin, a new project or a
 * finished task refreshes the sidebar by calling `reload()`. The alternative was a second interval
 * over the same connection for data nobody is watching.
 */

const EMPTY: SidebarView = {
  counts: {
    projects: null,
    clients: null,
    people: null,
    tasks: null,
    contracts: null,
    invoices: null,
    reports: null,
    worklogs: null,
    myOpenTasks: null,
    pendingApprovals: null,
    requests: null,
    deals: null,
  },
  projects: [],
  reports: [],
  deals: [],
  /**
   * ⚠️ **Empty rather than the registry's defaults, and the chrome's `!== false` is what makes that
   * safe.** Seeding this with `featureDefaults()` would state an answer nobody has asked the server
   * for yet; leaving it empty means "not answered", and `app.tsx` draws a flagged row until it is
   * told not to. A nav that rearranges itself one paint after it appears is worse than a row that
   * disappears on the next read.
   */
  features: {},
  /**
   * ⚠️⚠️ **TRUE BEFORE THE READ LANDS — THE SAME RULE `features` FOLLOWS, AND FOR ITS REASON.**
   * *Not answered yet* is not *no*: starting at false makes both rows appear one paint after the
   * sidebar draws, which is the rearranging nav the note above refuses — and the reachability gate
   * catches it, because a link that is not there on the first frame is not a link somebody can
   * click. The answer arrives with the first sidebar read and takes the row away then.
   */
  entitlements: {
    logsTime: true,
    leaveRequest: true,
    leaveDecide: true,
    leaveViewAll: true,
    menuProjects: true,
    menuTasks: true,
    menuContracts: true,
    menuInvoices: true,
    menuWorklogs: true,
    menuPeople: true,
    menuClients: true,
    reports: true,
    dashboardResource: true,
    dashboardLogging: true,
    dashboardUtilization: true,
    dashboardLeave: true,
  },
  /* Not answered yet: no banner until the read says there is something to say. */
  demoData: false,
};

export interface SidebarHandle {
  readonly view: SidebarView;
  /** 28 Oct 2026 · the reader's own logging issues — the *Idő* row's marks. `null` until read. */
  readonly myLogging: MyLoggingIssues | null;
  readonly reload: () => void;
  /** Whether this project is on the caller's own sidebar. */
  readonly isProjectPinned: (id: string) => boolean;
  readonly isReportPinned: (id: string) => boolean;
  /** Pin or unpin, then re-read. */
  readonly setProjectPinned: (id: string, pinned: boolean) => void;
  readonly setReportPinned: (id: string, pinned: boolean) => void;
}

export function useSidebar(): SidebarHandle {
  const session = useSession();
  const [view, setView] = useState<SidebarView>(EMPTY);
  const [myLogging, setMyLogging] = useState<MyLoggingIssues | null>(null);

  /* 28 Oct 2026 · its own read and its own topics: leave, a holiday or a schedule moves a short
     day, and none of those has any business reloading the rail's counts. */
  const loadMyLogging = useCallback(async (): Promise<void> => {
    const response = await session
      .request(`/dashboards/logging/me?today=${toWireDate(new Date())}`)
      .catch(() => null);
    if (response?.ok !== true) return;
    const parsed = myLoggingIssuesSchema.safeParse(await response.json().catch(() => null));
    if (parsed.success) setMyLogging(parsed.data);
  }, [session]);

  useEffect(() => {
    void loadMyLogging();
  }, [loadMyLogging]);

  useLiveInvalidation(MY_LOGGING_TOPICS, () => {
    void loadMyLogging();
  });

  const load = useCallback(async (): Promise<void> => {
    const response = await session.request('/workspace/sidebar').catch(() => null);
    /**
     * ⚠️ **A failure is silent, for `use-notifications`' reason exactly.** This wraps every screen;
     * a toast saying "could not load the sidebar" over somebody's work while the API restarts is
     * worse than a sidebar with no numbers on it. `EMPTY` renders as no counters and no pinned
     * lists, which is the honest rendering of *"we do not know yet"* — and every row still works,
     * because the rows come from `routes.tsx` rather than from this.
     */
    if (response?.ok !== true) return;

    const parsed = sidebarViewSchema.safeParse(await response.json().catch(() => null));
    if (!parsed.success) return;
    setView(parsed.data);
  }, [session]);

  useEffect(() => {
    void load();
  }, [load]);

  const reload = useCallback(() => {
    void load();
  }, [load]);

  /* ⚠⚠️ C21 · the counts in the rail are the most visible live surface in the product: a
   * number beside a list that disagrees with it is the complaint this chunk exists to remove. */
  useLiveInvalidation(SIDEBAR_TOPICS, reload);

  /**
   * ⚠️ **One helper for both directions, because the endpoint takes the desired STATE.**
   * `sidebar.ts` argues it: `{ favourite: true }` is idempotent where a toggle verb flips twice on a
   * double-click and leaves the star showing the opposite of what was meant.
   *
   * ⚠️ **It re-reads rather than patching local state.** The row carries an open-task count that
   * only the server can produce, so guessing the new list here would render a pinned project with no
   * number until the next navigation.
   */
  const set = useCallback(
    (kind: 'project' | 'report', id: string, pinned: boolean): void => {
      void session
        .request(`/workspace/favourites/${kind}/${encodeURIComponent(id)}`, {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ favourite: pinned }),
        })
        .then(() => load())
        .catch(() => undefined);
    },
    [session, load],
  );

  return useMemo(
    () => ({
      view,
      myLogging,
      reload,
      isProjectPinned: (id: string) => view.projects.some((project) => project.id === id),
      isReportPinned: (id: string) => view.reports.some((report) => report.id === id),
      setProjectPinned: (id: string, pinned: boolean) => {
        set('project', id, pinned);
      },
      setReportPinned: (id: string, pinned: boolean) => {
        set('report', id, pinned);
      },
    }),
    [view, myLogging, reload, set],
  );
}

/**
 * ⚠⚠️ **ONE COPY, SHARED BY THE CHROME AND EVERY SCREEN.** The shell draws the pinned lists and
 * the counters; the project and report lists draw a star per row that has to agree with them. Two
 * `useSidebar()` calls would mean two fetches of the same thing and two states that drift the moment
 * one of them pins something — the star would fill while the sidebar stayed empty.
 */
const SidebarContext = createContext<SidebarHandle | null>(null);

export function SidebarProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const handle = useSidebar();
  return <SidebarContext.Provider value={handle}>{children}</SidebarContext.Provider>;
}

/**
 * ⚠️ **Returns `null` outside the provider rather than throwing**, because the signed-out screens
 * render no chrome at all and a star on a screen with no sidebar is simply absent. A throw would
 * make the invitation page's crash depend on where it was mounted.
 */
export function useSidebarContext(): SidebarHandle | null {
  return useContext(SidebarContext);
}
